export type OptionalNumber = number | null | undefined

export type OptionalSize = [OptionalNumber, OptionalNumber]

/**
 * Determines whether size is larger than or equal to minimum size.
 */
export const getIsLargerThan = (
  size: OptionalSize,
  minimumSize: OptionalSize
) => {
  const width = size[0] ?? undefined
  const height = size[1] ?? undefined
  const minimumWidth = minimumSize[0] ?? undefined
  const minimumHeight = minimumSize[1] ?? undefined

  if (typeof width === 'undefined' || typeof height === 'undefined') {
    return true
  }

  // Compare 1 dimension
  if (
    typeof minimumWidth !== 'undefined' &&
    typeof minimumHeight === 'undefined'
  ) {
    return width >= minimumWidth || height >= minimumWidth
  }

  if (
    typeof minimumWidth === 'undefined' &&
    typeof minimumHeight !== 'undefined'
  ) {
    return width >= minimumHeight || height >= minimumHeight
  }

  // Compare 2 dimensions
  if (
    typeof minimumWidth !== 'undefined' &&
    typeof minimumHeight !== 'undefined'
  ) {
    return (
      (width >= minimumWidth && height >= minimumHeight) ||
      (height >= minimumWidth && width >= minimumHeight)
    )
  }

  return true
}

/**
 * Determines whether size is smaller than or equal to maximum size.
 */
export const getIsSmallerThan = (
  size: OptionalSize,
  maximumSize: OptionalSize
) => {
  const width = size[0] ?? undefined
  const height = size[1] ?? undefined
  const maximumWidth = maximumSize[0] ?? undefined
  const maximumHeight = maximumSize[1] ?? undefined

  if (typeof width === 'undefined' || typeof height === 'undefined') {
    return true
  }

  // Compare 1 dimension
  if (
    typeof maximumWidth !== 'undefined' &&
    typeof maximumHeight === 'undefined'
  ) {
    return width <= maximumWidth || height <= maximumWidth
  }

  if (
    typeof maximumWidth === 'undefined' &&
    typeof maximumHeight !== 'undefined'
  ) {
    return width <= maximumHeight || height <= maximumHeight
  }

  // Compare 2 dimensions
  if (
    typeof maximumWidth !== 'undefined' &&
    typeof maximumHeight !== 'undefined'
  ) {
    return (
      (width <= maximumWidth && height <= maximumHeight) ||
      (height <= maximumWidth && width <= maximumHeight)
    )
  }

  return true
}

/**
 * Determines whether size is larger than or equal to minimum size and smaller than or equal to maximum size.
 */
export const getIsSizeWithinBounds = (
  size: OptionalSize,
  minimumSize: OptionalSize,
  maximumSize: OptionalSize
) => getIsLargerThan(size, minimumSize) && getIsSmallerThan(size, maximumSize)

/**
 * Gets width and height from given string.
 */
export const getSizeDimensionsFromString = (str: string) => {
  const [width, height] = str
    .replaceAll(' ', '')
    .replaceAll(',', '.')
    .split('x')

  return {
    width: parseFloat(width),
    height: parseFloat(height),
  }
}
